import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import type { Organization, OrgConfigPatch } from '../types/domain';

export function useOrganizations() {
  return useQuery({
    queryKey: ['orgs'],
    queryFn: () => adminApi.organizations.list(),
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: ['orgs', id],
    queryFn: () => adminApi.organizations.get(id),
    enabled: Boolean(id),
  });
}

export function useOrgMembers(id: string) {
  return useQuery({
    queryKey: ['orgs', id, 'members'],
    queryFn: () => adminApi.organizations.listMembers(id),
    enabled: Boolean(id),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    // identifier y email son obligatorios (migración 007): la columna es
    // NOT NULL y el endpoint responde 400 si faltan.
    mutationFn: (body: {
      name: string;
      type: 'individual' | 'team';
      identifier: string;
      email: string;
    }) => adminApi.organizations.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
}

export function useDisableOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.organizations.disable(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['orgs', id] });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
}

export function useEnableOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminApi.organizations.enable(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['orgs', id] });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
}

export function useRunOrganization() {
  return useMutation({
    mutationFn: (id: string) => adminApi.organizations.run(id),
  });
}

export function useAssignMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: string; role: 'owner' | 'member' }) =>
      adminApi.organizations.assignMember(orgId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orgs', orgId] });
      void qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] });
    },
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => adminApi.organizations.removeMember(orgId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orgs', orgId] });
      void qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] });
    },
  });
}

export function useOrgConfig(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'config'],
    queryFn: () => adminApi.organizations.getConfig(orgId),
    enabled: !!orgId,
  });
}

export function useUpdateOrgConfig(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: OrgConfigPatch) => adminApi.organizations.updateConfig(orgId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orgs', orgId, 'config'] });
    },
  });
}

export type { Organization };
